if (!customElements.get('missence-background-video')) {
  customElements.define(
    'missence-background-video',
    class MissenceBackgroundVideo extends HTMLElement {
      constructor() {
        super();
        this.sync = this.sync.bind(this);
        this.onIntersection = this.onIntersection.bind(this);
        this.onToggleClick = this.onToggleClick.bind(this);
        this.onPlaying = this.onPlaying.bind(this);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.desktopViewport = window.matchMedia('(min-width: 990px)');
        this.isNearViewport = false;
        this.isPausedByUser = false;
        this.isRetryFromUser = false;
        this.isPlaybackBlocked = false;
      }

      connectedCallback() {
        this.videos = Array.from(this.querySelectorAll('video'));
        this.toggle = this.querySelector('[data-missence-background-video-toggle]');

        if (!this.videos.length) return;

        this.videos.forEach((video) => {
          video.muted = true;
          video.addEventListener('playing', this.onPlaying);
        });

        this.toggle?.addEventListener('click', this.onToggleClick);
        this.reducedMotion.addEventListener?.('change', this.sync);
        this.desktopViewport.addEventListener?.('change', this.sync);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(this.onIntersection, { rootMargin: '200px 0px' });
          this.observer.observe(this);
        } else {
          this.isNearViewport = true;
          this.sync();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.toggle?.removeEventListener('click', this.onToggleClick);
        this.reducedMotion.removeEventListener?.('change', this.sync);
        this.desktopViewport.removeEventListener?.('change', this.sync);
        this.videos?.forEach((video) => video.removeEventListener('playing', this.onPlaying));
      }

      onIntersection(entries) {
        this.isNearViewport = entries.some((entry) => entry.isIntersecting);
        this.sync();
      }

      onToggleClick() {
        this.isPausedByUser = !this.isPausedByUser;
        this.isRetryFromUser = !this.isPausedByUser;
        this.sync();
      }

      onPlayError(error, isFromUser) {
        if (error?.name === 'AbortError') return;

        this.isPausedByUser = true;
        if (isFromUser) this.isPlaybackBlocked = true;
        this.sync();
      }

      onPlaying(event) {
        if (event.currentTarget === this.getActiveVideo()) this.classList.add('is-playing');
      }

      getActiveVideo() {
        return this.videos.find((video) => video.getClientRects().length > 0) || null;
      }

      sync() {
        const activeVideo = this.getActiveVideo();
        const motionAllowed = !this.reducedMotion.matches;

        if (activeVideo !== this.currentVideo) {
          this.currentVideo = activeVideo;
          this.classList.remove('is-playing');
        }

        this.videos.forEach((video) => {
          if (video !== activeVideo) video.pause();
        });

        const canPlay = motionAllowed && activeVideo && !this.isPlaybackBlocked;

        if (this.toggle) {
          this.toggle.hidden = !canPlay;
          this.toggle.setAttribute('aria-pressed', this.isPausedByUser ? 'true' : 'false');
        }

        if (!canPlay) {
          activeVideo?.pause();
          this.classList.remove('is-playing');
          return;
        }

        if (this.isNearViewport && !this.isPausedByUser) {
          const isFromUser = this.isRetryFromUser;
          this.isRetryFromUser = false;
          activeVideo.play()?.catch((error) => this.onPlayError(error, isFromUser));
        } else {
          activeVideo.pause();
        }
      }
    }
  );
}
